import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, X } from "lucide-react";

export default function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title = "Are you sure?",
  message = "Do you really want to perform this action? This process cannot be undone.",
  confirmText = "Confirm",
  cancelText = "Cancel",
  loading
}) {
  const [theme, setTheme] = useState("dark");

  useEffect(() => {
    const savedTheme = localStorage.getItem("vite-ui-theme");
    if (savedTheme === "light") {
      setTheme("light");
    } else {
      setTheme("dark");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className={`relative w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden ${
            theme === "dark"
              ? "bg-gray-900/90 text-gray-100"
              : "bg-white/90 text-gray-900"
          }`}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ type: "spring", stiffness: 250, damping: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </button>

          <div className="p-6">
            <h2 className="text-2xl font-bold mb-2">{title}</h2>
            <p className="text-lg text-gray-500 dark:text-gray-400">
              {message}
            </p>

            <div className="flex justify-end gap-3 mt-8">
              <button
                onClick={onClose}
                className={`px-4 py-2 rounded-xl border text-lg font-medium transition-all
                  ${
                    theme === "dark"
                      ? "border-gray-700 text-gray-300 hover:bg-gray-800"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
              >
                {cancelText}
              </button>
              <button
                onClick={onConfirm}
                disabled={loading}
                className={`px-5 py-2 rounded-xl bg-gradient-to-r from-primary to-accent text-white text-lg font-semibold shadow-md transition
                  hover:opacity-90 active:scale-95
                  ${loading ? "opacity-70 cursor-not-allowed" : ""}
                `}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Processing...</span>
                  </div>
                ) : (
                  confirmText
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
